/**
 * Filter Builder Utility
 * Provides a fluent API for building filter strings according to the new API specification
 */

import { Geometry } from 'geojson';
import {
  BooleanFilterBuilder,
  DateFilterBuilder,
  FilterField,
  FilterGroup,
  FilterOperator,
  JsonFilterBuilder,
  LogicalOperator,
  NumericFilterBuilder,
  SortConfig,
  SortDirection,
  SpatialDistanceValue,
  SpatialFilterBuilder,
  SpatialOperator,
  TextFilterBuilder
} from '../models/filter.models';

export class FilterBuilderUtil {
  /**
   * Creates a text filter builder for string fields
   */
  static text(field: string): TextFilterBuilder {
    return new TextFilterBuilderImpl(field);
  }

  /**
   * Creates a numeric filter builder for number fields
   */
  static numeric(field: string): NumericFilterBuilder {
    return new NumericFilterBuilderImpl(field);
  }

  /**
   * Creates a date filter builder for date/datetime fields
   */
  static date(field: string): DateFilterBuilder {
    return new DateFilterBuilderImpl(field);
  }

  /**
   * Creates a boolean filter builder for boolean fields
   */
  static boolean(field: string): BooleanFilterBuilder {
    return new BooleanFilterBuilderImpl(field);
  }

  /**
   * Creates a spatial filter builder for geometry fields
   */
  static spatial(field: string): SpatialFilterBuilder {
    return new SpatialFilterBuilderImpl(field);
  }

  /**
   * Creates a JSON filter builder for JSON fields
   */
  static json(field: string): JsonFilterBuilder {
    return new JsonFilterBuilderImpl(field);
  }

  /**
   * Creates a filter group with AND logic
   */
  static and(...conditions: (FilterField | FilterGroup)[]): FilterGroup {
    return {
      operator: LogicalOperator.AND,
      conditions
    };
  }

  /**
   * Creates a filter group with OR logic
   */
  static or(...conditions: (FilterField | FilterGroup)[]): FilterGroup {
    return {
      operator: LogicalOperator.OR,
      conditions
    };
  }

  /**
   * Creates a sort configuration
   */
  static sort(
    field: string,
    direction: SortDirection = SortDirection.ASC
  ): SortConfig {
    return { field, direction };
  }

  /**
   * Converts filter fields to a filter string
   */
  static toFilterString(filters: FilterField[]): string {
    return filters.map((filter) => this.filterFieldToString(filter)).join(',');
  }

  static filterGroupToString(filterGroup: FilterGroup): string {
    const conditions = filterGroup.conditions.map((condition) => {
      if ('operator' in condition && 'conditions' in condition) {
        return `(${this.filterGroupToString(condition as FilterGroup)})`;
      } else {
        if (filterGroup.operator === LogicalOperator.OR) {
          return `(${this.filterFieldToString(condition as FilterField)})`;
        }
        return this.filterFieldToString(condition as FilterField);
      }
    });

    if (filterGroup.operator === LogicalOperator.OR) {
      return conditions.join('|');
    } else {
      return conditions.join(',');
    }
  }

  /**
   * Converts a single filter field to string
   */
  static filterFieldToString(filter: FilterField): string {
    const { field, operator, value } = filter;

    if (
      operator === FilterOperator.IS_NULL ||
      operator === FilterOperator.IS_NOT_NULL
    ) {
      return `${field}:${operator}:true`;
    }

    if (operator === SpatialOperator.ST_DWITHIN) {
      const spatialValue = value as SpatialDistanceValue;
      const valueStr = JSON.stringify(spatialValue);
      return `${field}:${operator}:${valueStr}`;
    }

    if (typeof value === 'object' && value !== null) {
      return `${field}:${operator}:${JSON.stringify(value)}`;
    }

    if (Array.isArray(value)) {
      return `${field}:${operator}:${value.join('|')}`;
    }

    return `${field}:${operator}:${value}`;
  }

  /**
   * Converts sort configurations to sort string
   */
  static toSortString(sorts: SortConfig[]): string {
    return sorts.map((sort) => `${sort.field}:${sort.direction}`).join(',');
  }

  /**
   * URL encodes filter and sort strings for use in query parameters
   */
  static encodeForUrl(value: string): string {
    return encodeURIComponent(value);
  }

  /**
   * Decodes URL-encoded filter and sort strings
   */
  static decodeFromUrl(value: string): string {
    return decodeURIComponent(value);
  }
}

// Implementation classes for the filter builders

class TextFilterBuilderImpl implements TextFilterBuilder {
  constructor(private field: string) {}

  equals(value: string): FilterField {
    return { field: this.field, operator: FilterOperator.EQ, value };
  }

  contains(value: string): FilterField {
    return { field: this.field, operator: FilterOperator.CONTAINS, value };
  }

  startsWith(value: string): FilterField {
    return {
      field: this.field,
      operator: FilterOperator.LIKE,
      value: `${value}%`
    };
  }

  endsWith(value: string): FilterField {
    return {
      field: this.field,
      operator: FilterOperator.LIKE,
      value: `%${value}`
    };
  }

  like(value: string): FilterField {
    return { field: this.field, operator: FilterOperator.LIKE, value };
  }

  ilike(value: string): FilterField {
    // Auto-add wildcards if not present
    const processedValue = value.includes('%') ? value : `%${value}%`;
    return {
      field: this.field,
      operator: FilterOperator.ILIKE,
      value: processedValue
    };
  }

  in(values: string[]): FilterField {
    return { field: this.field, operator: FilterOperator.IN, value: values };
  }

  notIn(values: string[]): FilterField {
    return {
      field: this.field,
      operator: FilterOperator.NOT_IN,
      value: values
    };
  }

  isNull(): FilterField {
    return { field: this.field, operator: FilterOperator.IS_NULL, value: null };
  }

  isNotNull(): FilterField {
    return {
      field: this.field,
      operator: FilterOperator.IS_NOT_NULL,
      value: null
    };
  }
}

class NumericFilterBuilderImpl implements NumericFilterBuilder {
  constructor(private field: string) {}

  equals(value: number): FilterField {
    return { field: this.field, operator: FilterOperator.EQ, value };
  }

  greaterThan(value: number): FilterField {
    return { field: this.field, operator: FilterOperator.GT, value };
  }

  greaterThanOrEqual(value: number): FilterField {
    return { field: this.field, operator: FilterOperator.GTE, value };
  }

  lessThan(value: number): FilterField {
    return { field: this.field, operator: FilterOperator.LT, value };
  }

  lessThanOrEqual(value: number): FilterField {
    return { field: this.field, operator: FilterOperator.LTE, value };
  }

  in(values: number[]): FilterField {
    return { field: this.field, operator: FilterOperator.IN, value: values };
  }

  notIn(values: number[]): FilterField {
    return {
      field: this.field,
      operator: FilterOperator.NOT_IN,
      value: values
    };
  }

  between(min: number, max: number): FilterField[] {
    return [this.greaterThanOrEqual(min), this.lessThanOrEqual(max)];
  }
}

class DateFilterBuilderImpl implements DateFilterBuilder {
  constructor(private field: string) {}

  equals(value: Date | string): FilterField {
    const dateValue = this.formatDate(value);
    return { field: this.field, operator: FilterOperator.EQ, value: dateValue };
  }

  after(value: Date | string): FilterField {
    const dateValue = this.formatDate(value);
    return { field: this.field, operator: FilterOperator.GT, value: dateValue };
  }

  before(value: Date | string): FilterField {
    const dateValue = this.formatDate(value);
    return { field: this.field, operator: FilterOperator.LT, value: dateValue };
  }

  between(start: Date | string, end: Date | string): FilterField[] {
    return [this.after(start), this.before(end)];
  }

  isNull(): FilterField {
    return { field: this.field, operator: FilterOperator.IS_NULL, value: null };
  }

  isNotNull(): FilterField {
    return {
      field: this.field,
      operator: FilterOperator.IS_NOT_NULL,
      value: null
    };
  }

  private formatDate(value: Date | string): string {
    if (value instanceof Date) {
      return value.toISOString();
    }
    return value;
  }
}

class BooleanFilterBuilderImpl implements BooleanFilterBuilder {
  constructor(private field: string) {}

  equals(value: boolean): FilterField {
    return { field: this.field, operator: FilterOperator.EQ, value };
  }

  isTrue(): FilterField {
    return { field: this.field, operator: FilterOperator.EQ, value: true };
  }

  isFalse(): FilterField {
    return { field: this.field, operator: FilterOperator.EQ, value: false };
  }
}

class SpatialFilterBuilderImpl implements SpatialFilterBuilder {
  constructor(private field: string) {}

  intersects(geometry: Geometry): FilterField {
    return {
      field: this.field,
      operator: SpatialOperator.ST_INTERSECTS,
      value: geometry
    };
  }

  contains(geometry: Geometry): FilterField {
    return {
      field: this.field,
      operator: SpatialOperator.ST_CONTAINS,
      value: geometry
    };
  }

  within(geometry: Geometry): FilterField {
    return {
      field: this.field,
      operator: SpatialOperator.ST_WITHIN,
      value: geometry
    };
  }

  withinDistance(
    geometry: Geometry,
    distance: number,
    unit: 'meters' | 'kilometers' | 'feet' | 'miles' = 'meters'
  ): FilterField {
    const spatialValue: SpatialDistanceValue = { geometry, distance, unit };
    return {
      field: this.field,
      operator: SpatialOperator.ST_DWITHIN,
      value: spatialValue
    };
  }

  overlaps(geometry: Geometry): FilterField {
    return {
      field: this.field,
      operator: SpatialOperator.ST_OVERLAPS,
      value: geometry
    };
  }

  touches(geometry: Geometry): FilterField {
    return {
      field: this.field,
      operator: SpatialOperator.ST_TOUCHES,
      value: geometry
    };
  }

  crosses(geometry: Geometry): FilterField {
    return {
      field: this.field,
      operator: SpatialOperator.ST_CROSSES,
      value: geometry
    };
  }
}

class JsonFilterBuilderImpl implements JsonFilterBuilder {
  private currentPath: string;

  constructor(private field: string) {
    this.currentPath = field;
  }

  path(path: string): JsonFilterBuilder {
    this.currentPath = `${this.field}.${path}`;
    return this;
  }

  equals(value: any): FilterField {
    return { field: this.currentPath, operator: FilterOperator.EQ, value };
  }

  contains(value: any): FilterField {
    return {
      field: this.currentPath,
      operator: FilterOperator.CONTAINS,
      value
    };
  }

  exists(): FilterField {
    return {
      field: this.currentPath,
      operator: FilterOperator.EXISTS,
      value: true
    };
  }

  isNull(): FilterField {
    return {
      field: this.currentPath,
      operator: FilterOperator.IS_NULL,
      value: null
    };
  }

  isNotNull(): FilterField {
    return {
      field: this.currentPath,
      operator: FilterOperator.IS_NOT_NULL,
      value: null
    };
  }

  greaterThan(value: number): FilterField {
    return { field: this.currentPath, operator: FilterOperator.GT, value };
  }

  lessThan(value: number): FilterField {
    return { field: this.currentPath, operator: FilterOperator.LT, value };
  }

  in(values: any[]): FilterField {
    return {
      field: this.currentPath,
      operator: FilterOperator.IN,
      value: values
    };
  }
}
